import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense, lazy, useCallback, useMemo } from 'react';
import { PageLoading } from 'components/atoms/loading';
import { LocaleIntlProvider } from 'components/i18n/LocaleIntlProvider';
import { MIGRATION_AGENTS_MESH, NITRO_ANALYSTS_MESH, NITRO_BREWS_MESH, NITRO_CLAW_MESH, RM_AGENTS_MESH, WORKFORCE_AGENTS_MESH } from 'components/organisms/nitro-claw/nitro-claw-mesh.config';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAppShellMode } from 'hooks/use-app-shell-mode';
import { useAiFeatureFlags } from 'hooks/use-feature-flags';
const MESH_PRESETS = {
    'nitro-brews': NITRO_BREWS_MESH,
    'nitro-analysts': NITRO_ANALYSTS_MESH,
    'nitro-claw': NITRO_CLAW_MESH,
    migration: MIGRATION_AGENTS_MESH,
    workforce: WORKFORCE_AGENTS_MESH,
    rm: RM_AGENTS_MESH
};
// Lazy: MeshSphere pulls meshy + @rocketlane/ui; only needed for claw chrome.
const MeshSphere = /*#__PURE__*/ lazy(()=>import('vendor/meshy').then((m)=>({
            default: m.MeshSphere
        })));
// Lazy: SheetInChatView is only invoked when the remote host renders a
// 'sheet_instance' request. Static import was pinning the whole sheets
// graph into the shell.
const SheetInChatView = /*#__PURE__*/ lazy(()=>import('components/organisms/sheets/sheet-in-chat/sheet-in-chat-view').then((m)=>({
            default: m.SheetInChatView
        })));
// Lazy: ActionAgentSection drags the automations filter UI which pulls
// the tasks-organism criteria tree (~84 kB). Only rendered when the host
// requests an 'agent_trigger' component.
const ActionAgentSection = /*#__PURE__*/ lazy(()=>import('components/organisms/agent-trigger/components/action-agent-section').then((m)=>({
            default: m.ActionAgentSection
        })));
/**
 * Builds the hostAPI (renderComponent) used by the agent builder remote and
 * document agent preview. Share this so preview route has the same HostAPI
 * as the main agent builder (fixes "useHostAPI must be used within a HostAPIProvider").
 */ export function useAgentBuilderHostAPI() {
    const { hasAccessToResource } = useWithAccess();
    const { isSignalsEnabled } = useAiFeatureFlags();
    const { isNitroClawShellActive } = useAppShellMode();
    const renderComponent = useCallback((request)=>{
        switch(request.type){
            case 'sheet_instance':
                {
                    const props = request.props;
                    return /*#__PURE__*/ _jsx(LocaleIntlProvider, {
                        fallbackOnEmptyString: true,
                        children: /*#__PURE__*/ _jsx(Suspense, {
                            fallback: null,
                            children: /*#__PURE__*/ _jsx(SheetInChatView, {
                                projectId: props.projectId,
                                sheetId: props.sheetId,
                                versionId: props.versionId,
                                tabId: props.sheetTabId,
                                readonly: props.readonly,
                                showInfoBar: props.showInfoBar,
                                sourceType: props.sourceType,
                                sourceId: props.sourceId,
                                ruleIds: props.ruleIds,
                                conversationId: props.conversationId,
                                validationType: props.validationType
                            })
                        })
                    });
                }
            case 'agent_trigger':
                {
                    const props = request.props;
                    return /*#__PURE__*/ _jsx(LocaleIntlProvider, {
                        fallbackOnEmptyString: true,
                        children: /*#__PURE__*/ _jsx(Suspense, {
                            fallback: null,
                            children: /*#__PURE__*/ _jsx(ActionAgentSection, {
                                shouldShowActionCheckbox: props.shouldShowActionCheckbox,
                                actionEnabled: props.actionEnabled,
                                onActionEnabledChange: props.onActionEnabledChange,
                                agentTriggerRegistryConfig: props.agentTriggerRegistryConfig,
                                onAgentTriggerRegistryConfigChange: props.onAgentTriggerRegistryConfigChange,
                                defaultDocumentAgentId: props.defaultDocumentAgentId,
                                onAddConditions: props.onAddConditions,
                                isSignalsEnabled: isSignalsEnabled
                            })
                        })
                    });
                }
            case 'loading':
                {
                    const props = request.props;
                    return /*#__PURE__*/ _jsx(LocaleIntlProvider, {
                        fallbackOnEmptyString: true,
                        children: /*#__PURE__*/ _jsx(PageLoading, {
                            size: props.size
                        })
                    });
                }
            case 'mesh_sphere':
                {
                    var _props_colors, _props_meshId;
                    const props = request.props;
                    const preset = props.preset ? MESH_PRESETS[props.preset] : undefined;
                    const colors = (_props_colors = props.colors) !== null && _props_colors !== void 0 ? _props_colors : preset === null || preset === void 0 ? void 0 : preset.colors;
                    const meshId = (_props_meshId = props.meshId) !== null && _props_meshId !== void 0 ? _props_meshId : preset === null || preset === void 0 ? void 0 : preset.meshId;
                    if (!colors || meshId == null) {
                        return null;
                    }
                    return /*#__PURE__*/ _jsx(Suspense, {
                        fallback: null,
                        children: /*#__PURE__*/ _jsx(MeshSphere, {
                            colors: colors,
                            meshId: meshId,
                            size: props.size,
                            animated: props.animated,
                            className: props.className,
                            "data-cy": props['data-cy']
                        })
                    });
                }
            default:
                return null;
        }
    }, [
        isSignalsEnabled
    ]);
    return useMemo(()=>({
            renderComponent,
            hasAccessToResource,
            shell: isNitroClawShellActive ? 'nitro-claw' : 'rocketlane'
        }), [
        renderComponent,
        hasAccessToResource,
        isNitroClawShellActive
    ]);
}
